import { useQuery } from '@tanstack/react-query'
import { authClient } from '../auth'
import { api } from './api'
import { setSelectedPortalContext } from './organizationSelection'

export type PortalContextKey = { kind: 'creator' | 'organization'; id: string }
export type PortalContext = PortalContextKey & {
  displayName: string
  avatarUrl?: string | null
}
export type ApplicationContext = {
  kind: 'application'
  id: string
  displayName: ''
}
export type SelectedPortalContext = PortalContext | ApplicationContext
export type PortalContextsResponse = {
  contexts: PortalContext[]
  defaultContext: PortalContextKey | null
}

const STORAGE_PREFIX = 'bocify.portal-context'

export function portalContextStorageKey(userId: string) {
  return `${STORAGE_PREFIX}.${userId}`
}

function matchingContext(contexts: PortalContext[], value: unknown): PortalContext | null {
  if (!value || typeof value !== 'object') return null
  const candidate = value as { kind?: unknown; id?: unknown }
  if (
    (candidate.kind !== 'creator' && candidate.kind !== 'organization') ||
    typeof candidate.id !== 'string'
  )
    return null
  return (
    contexts.find((context) => context.kind === candidate.kind && context.id === candidate.id) ??
    null
  )
}

export function loadReconciledContext(
  userId: string,
  response: PortalContextsResponse,
): SelectedPortalContext {
  let persisted: unknown = null
  try {
    const raw = localStorage.getItem(portalContextStorageKey(userId))
    persisted = raw ? JSON.parse(raw) : null
  } catch {
    persisted = null
  }

  const selected =
    matchingContext(response.contexts, persisted) ??
    matchingContext(response.contexts, response.defaultContext)
  if (!selected) {
    clearPersistedPortalContext(userId)
    const application = {
      kind: 'application' as const,
      id: userId,
      displayName: '' as const,
    }
    setSelectedPortalContext(application)
    return application
  }
  persistPortalContext(userId, selected)
  setSelectedPortalContext(selected)
  return selected
}

export function persistPortalContext(userId: string, context: PortalContextKey) {
  try {
    localStorage.setItem(
      portalContextStorageKey(userId),
      JSON.stringify({ kind: context.kind, id: context.id }),
    )
  } catch {
    // Storage can be unavailable in privacy modes. The authoritative response
    // still initializes the in-memory context for this page load.
  }
}

export function clearPersistedPortalContext(userId: string) {
  try {
    localStorage.removeItem(portalContextStorageKey(userId))
  } catch {
    // Signing out and authoritative reconciliation must not depend on storage.
  }
}

export function usePortalContext() {
  const { data: session } = authClient.useSession()
  const userId = session?.user?.id
  const query = useQuery({
    queryKey: ['portal-contexts', userId],
    enabled: Boolean(userId),
    // Reconciliation is effectful (writes storage, initializes the in-memory
    // tenant singleton), so it lives in the queryFn — once per fetch — rather
    // than in `select`, which TanStack Query requires to be pure and which
    // re-runs on every render. Running it here also guarantees the singleton
    // is set before any org query gated on this data can fire.
    queryFn: async () => {
      const response = await api.get<PortalContextsResponse>('/partners/contexts')
      return { response, selected: loadReconciledContext(userId!, response) }
    },
  })
  return { ...query, userId }
}
